import { useMemo } from 'react';
import { useAuthStore } from '../stores/authStore';
import { ROUTE_PERMISSIONS } from '../config/routes';

// 角色层级定义
const roleHierarchy: Record<string, number> = {
  owner: 4,
  admin: 3,
  operator: 2,
  viewer: 1,
};

// 角色权限映射（与后端 permission.go rolePermissions 保持一致）
const rolePermissions: Record<string, string[]> = {
  owner: ['*'], // 全部权限
  admin: [
    'org:read', 'org:update', 'org:member:manage', 'org:role:assign',
    'proposal:read', 'proposal:write', 'proposal:delete', 'proposal:export',
    'customer:read', 'customer:write', 'customer:delete',
    'scout:read', 'scout:write', 'scout:search', 'scout:evaluate',
    'outreach:read', 'outreach:write', 'outreach:generate', 'outreach:send',
    'knowledge:read', 'knowledge:write', 'knowledge:admin', 'knowledge:import',
    'notification:read',
    'settings:read', 'settings:write',
    'member:read', 'member:write',
  ],
  operator: [
    'proposal:read', 'proposal:write', 'proposal:export',
    'customer:read', 'customer:write',
    'scout:read', 'scout:write', 'scout:search', 'scout:evaluate',
    'outreach:read', 'outreach:write', 'outreach:generate', 'outreach:send',
    'knowledge:read', 'knowledge:write',
    'notification:read',
  ],
  viewer: [
    'proposal:read',
    'customer:read',
    'scout:read',
    'outreach:read',
    'knowledge:read',
    'notification:read',
  ],
};

/**
 * 匹配权限字符串，支持通配符
 * 例如: 'proposal:*' 匹配 'proposal:read', 'proposal:write' 等
 */
function matchPermission(held: string, required: string): boolean {
  if (held === '*') return true;
  if (held === required) return true;

  // 支持模块级通配符: 'proposal:*' 匹配 'proposal:read'
  if (held.endsWith(':*')) {
    const module = held.slice(0, -2);
    return required.startsWith(module + ':');
  }

  return false;
}

/**
 * 权限 Hook
 * 提供权限检查、角色判断、数据所有者校验等功能
 */
export function usePermission() {
  const { user } = useAuthStore();
  const role = (user?.role as string) || 'viewer';
  const userId = user?.id;

  /**
   * 检查是否拥有指定权限
   * @param requiredPermission 需要的权限，格式为 "module:action"
   * 支持通配符匹配
   */
  const hasPermission = (requiredPermission: string): boolean => {
    const permissions = rolePermissions[role];
    if (!permissions) return false;

    return permissions.some((p) => matchPermission(p, requiredPermission));
  };

  /**
   * 检查是否拥有任一权限
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some((p) => hasPermission(p));
  };

  /**
   * 检查是否拥有所有权限
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every((p) => hasPermission(p));
  };

  /**
   * 检查角色层级是否达到要求
   */
  const hasRoleLevel = (requiredRole: string): boolean => {
    const currentLevel = roleHierarchy[role] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;
    return currentLevel >= requiredLevel;
  };

  /**
   * 检查是否可以访问指定路由
   */
  const canAccessRoute = (pathname: string): boolean => {
    const permissions = ROUTE_PERMISSIONS[pathname];
    if (!permissions) return true; // 未定义权限的路由默认可访问
    return permissions.includes(role);
  };

  /**
   * 检查是否可以删除资源
   * owner/admin 可以删除任何数据
   * operator 只能删除自己创建的数据
   * viewer 不可删除
   * @param createdBy 资源创建者的 userID
   */
  const canDelete = (createdBy?: string): boolean => {
    // owner 和 admin 可以删除任何数据
    if (role === 'owner' || role === 'admin') return true;
    // viewer 无删除权限
    if (role === 'viewer') return false;
    // operator 只能删除自己创建的数据
    if (!createdBy || !userId) return false;
    return createdBy === userId;
  };

  /**
   * 检查是否可以编辑资源
   * owner/admin 可以编辑任何数据
   * operator 可以编辑（取决于具体权限配置）
   * viewer 不可编辑
   * @param createdBy 资源创建者的 userID（可选，用于更严格的所有者校验）
   */
  const canEdit = (createdBy?: string): boolean => {
    if (role === 'owner' || role === 'admin') return true;
    if (role === 'viewer') return false;
    // operator 默认可以编辑，如需所有者限制可传入 createdBy
    if (createdBy && userId) return createdBy === userId;
    return true;
  };

  // 角色判断（使用 useMemo 避免每次渲染重新计算）
  const isOwner = role === 'owner';
  const isAdmin = role === 'owner' || role === 'admin';
  const canWrite = role !== 'viewer';
  const isOperator = role === 'operator';
  const isViewer = role === 'viewer';
  const isSuperAdmin = user?.is_super_admin === true;

  return useMemo(() => ({
    role,
    userId,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRoleLevel,
    canAccessRoute,
    canDelete,
    canEdit,
    isOwner,
    isAdmin,
    canWrite,
    isOperator,
    isViewer,
    isSuperAdmin,
    roleLevel: roleHierarchy[role] || 0,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [role, userId]);
}

export type PermissionHook = ReturnType<typeof usePermission>;
